// src/Components/Modules/HR/DocumentManager.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box, Typography, Paper, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, Chip, IconButton, Tooltip,
  Skeleton, Alert, Stack, Avatar, TextField, InputAdornment, MenuItem, Divider,
  FormControl, InputLabel, Select, Dialog, DialogTitle, DialogContent,
  DialogActions, Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import {
  FaUpload, FaTrash, FaFile, FaFileAlt, FaFileContract, FaIdCard,
  FaGraduationCap, FaUsers, FaFolderOpen, FaDownload, FaEye, FaSearch,
  FaSync, FaExclamationTriangle, FaCalendarAlt, FaPlus, FaTh, FaList,
  FaUserShield, FaLock, FaUnlock, FaExpand, FaSort, FaSortUp, FaSortDown,
  FaTimes,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

import DocumentViewer from "./DocumentViewer";

// ============== Styled Components ==============
const MainContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: "#f8fafc",
  minHeight: "100vh",
}));

const HeaderCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(3),
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  boxShadow: "0 4px 20px rgba(102, 126, 234, 0.15)",
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(1.5),
  backgroundColor: "white",
  border: "1px solid #e2e8f0",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
    borderColor: "#cbd5e1",
  },
}));

const ViewToggleButton = styled(IconButton)(({ theme, active }) => ({
  borderRadius: theme.spacing(1),
  color: active ? "#667eea" : "#64748b",
  backgroundColor: active ? "#f0f4ff" : "transparent",
  "&:hover": { backgroundColor: active ? "#e0e7ff" : "#f1f5f9" },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1, 2.5),
  boxShadow: "none",
  "&:hover": { boxShadow: "0 4px 12px rgba(102, 126, 234, 0.2)" },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  border: "1px solid #e2e8f0",
  backgroundColor: "white",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: "#475569",
  borderBottom: "2px solid #e2e8f0",
  padding: theme.spacing(1.5, 2),
  fontSize: "0.875rem",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": { backgroundColor: "#f8fafc" },
  transition: "background-color 0.15s ease",
}));

const DocumentCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  border: "1px solid #e2e8f0",
  transition: "all 0.2s ease",
  "&:hover": { boxShadow: "0 8px 16px rgba(0,0,0,0.08)", borderColor: "#cbd5e1" },
}));

// ============== Permission chip config ==============
const PERMISSION_STYLES = {
  private: { bg: "#fef2f2", color: "#dc2626" },
  restricted: { bg: "#fef3c7", color: "#d97706" },
  public: { bg: "#f0fdf4", color: "#16a34a" },
};

const permissionIcon = (v) =>
  v === "private" ? <FaLock /> : v === "restricted" ? <FaUserShield /> : <FaUnlock />;

const renderPermissionChip = (visibility, size = "small") => {
  const v = visibility || "private";
  const s = PERMISSION_STYLES[v] || PERMISSION_STYLES.private;
  return (
    <Chip
      icon={permissionIcon(v)}
      label={v}
      size={size}
      sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600, textTransform: "capitalize", "& .MuiChip-icon": { color: s.color } }}
    />
  );
};

// ============== Main Component ==============
const DocumentManager = () => {
  // ---- State ----
  const [docs, setDocs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [viewMode, setViewMode] = useState("table");
  const [selectedEmployee, setSelectedEmployee] = useState("all");

  const [employeeId, setEmployeeId] = useState("");
  const [documents, setDocuments] = useState([
    { type: "contract", file: null, caption: "", issue_date: "", visibility: "private" },
  ]);

  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Employee "View All Documents" dialog
  const [employeeDialogOpen, setEmployeeDialogOpen] = useState(false);
  const [dialogEmployeeId, setDialogEmployeeId] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);

  const role = parseInt(localStorage.getItem("who_am_i"), 10);
  const isEmployee = role === 4;

  // ---- Data Loading ----
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (role === 4) {
        const docsRes = await AxiosAuthInstance.get("hr/documents/my-documents");
        setDocs(Array.isArray(docsRes.data) ? docsRes.data : []);
      } else {
        const [docsRes, employeesRes] = await Promise.all([
          AxiosAuthInstance.get("hr/documents"),
          AxiosAuthInstance.get("add_employee"),
        ]);
        setDocs(Array.isArray(docsRes.data) ? docsRes.data : []);
        setEmployees(
          Array.isArray(employeesRes.data.employee)
            ? employeesRes.data.employee.map((x) => ({
                label: x.employee_name,
                value: x.id,
                department: x.department,
                position: x.position,
              }))
            : []
        );
      }
    } catch (err) {
      console.error("Document load error:", err);
      setError("Failed to load documents");
      Swal.fire({ icon: "error", title: "Error", text: "Failed to load document data." });
    } finally {
      setLoading(false);
    }
  };

  // ---- Upload Logic ----
  const addMoreDocument = () =>
    setDocuments([...documents, { type: "contract", file: null, caption: "", issue_date: "", visibility: "private" }]);

  const removeDocument = (index) => {
    if (documents.length > 1) setDocuments(documents.filter((_, i) => i !== index));
  };

  const handleDocChange = (index, field, value) => {
    const newDocs = [...documents];
    newDocs[index][field] = value;
    setDocuments(newDocs);
  };

  const validateForm = () => {
    if (!employeeId) {
      Swal.fire({ icon: "warning", title: "Missing", text: "Please select an employee" });
      return false;
    }
    for (let i = 0; i < documents.length; i++) {
      if (!documents[i].type) {
        Swal.fire({ icon: "warning", title: "Missing", text: `Type for document ${i + 1}` });
        return false;
      }
      if (!documents[i].file) {
        Swal.fire({ icon: "warning", title: "Missing", text: `File for document ${i + 1}` });
        return false;
      }
    }
    return true;
  };

  const upload = async () => {
    if (!validateForm()) return;
    setUploadLoading(true);
    try {
      const fd = new FormData();
      fd.append("employee_id", employeeId);
      documents.forEach((doc, index) => {
        fd.append(`documents[${index}][type]`, doc.type);
        fd.append(`documents[${index}][file]`, doc.file);
        fd.append(`documents[${index}][caption]`, doc.caption || "");
        fd.append(`documents[${index}][issue_date]`, doc.issue_date || "");
        fd.append(`documents[${index}][visibility]`, doc.visibility || "private");
      });
      await AxiosAuthInstance.post("hr/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.fire({ icon: "success", title: "Uploaded!", timer: 1800, showConfirmButton: false });
      setEmployeeId("");
      setDocuments([{ type: "contract", file: null, caption: "", issue_date: "", visibility: "private" }]);
      loadData();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.message || "Failed to upload" });
    } finally {
      setUploadLoading(false);
    }
  };

  const deleteDocument = async (id) => {
    const result = await Swal.fire({
      title: "Delete Document?",
      text: "This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete",
    });
    if (result.isConfirmed) {
      setActionLoading(id);
      try {
        await AxiosAuthInstance.delete(`hr/documents/${id}`);
        Swal.fire({ icon: "success", title: "Deleted", timer: 1800, showConfirmButton: false });
        setDocs((d) => d.filter((x) => x.id !== id));
      } catch (err) {
        Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.message || "Failed to delete" });
      } finally {
        setActionLoading(null);
      }
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await AxiosAuthInstance.get(`hr/documents/${doc.id}/download`, { responseType: "blob" });
      const fileName = doc.file_path ? doc.file_path.split("/").pop() : "document";
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      Swal.fire({ icon: "success", title: "Download started", timer: 1500, showConfirmButton: false, toast: true, position: "top-end" });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: "Failed to download" });
    }
  };

  const openEmployeeDocs = (group) => {
    setDialogEmployeeId(group.employee?.id);
    setEmployeeDialogOpen(true);
  };

  const switchView = (mode) => {
    setViewMode(mode);
    setPage(0);
  };

  // ---- Memos (ALL hooks at top level) ----
  const filtered = useMemo(() => {
    if (!Array.isArray(docs)) return [];
    return docs.filter((item) => {
      const employeeName = item.employee?.employee_name?.toLowerCase() || "";
      const type = item.type?.toLowerCase() || "";
      const visibility = item.visibility?.toLowerCase() || "private";
      const searchTerm = search.toLowerCase();
      const matchesSearch = employeeName.includes(searchTerm) || item.caption?.toLowerCase().includes(searchTerm);
      const matchesType = typeFilter === "all" || type === typeFilter;
      const matchesVisibility = visibilityFilter === "all" || visibility === visibilityFilter;
      const matchesEmployee = selectedEmployee === "all" || item.employee?.id === parseInt(selectedEmployee);
      return matchesSearch && matchesType && matchesVisibility && matchesEmployee;
    });
  }, [docs, search, typeFilter, visibilityFilter, selectedEmployee]);

  // ✅ ONE ROW PER EMPLOYEE — group filtered docs by employee
  const employeeGroups = useMemo(() => {
    const grouped = {};
    filtered.forEach((doc) => {
      const empId = doc.employee?.id;
      if (!empId) return;
      if (!grouped[empId]) grouped[empId] = { employee: doc.employee, documents: [] };
      grouped[empId].documents.push(doc);
    });
    let groups = Object.values(grouped);
    if (sortConfig.key === "employee_name") {
      groups.sort((a, b) => {
        const cmp = (a.employee?.employee_name || "").localeCompare(b.employee?.employee_name || "");
        return sortConfig.direction === "asc" ? cmp : -cmp;
      });
    }
    return groups;
  }, [filtered, sortConfig]);

  const paginatedGroups = useMemo(
    () => employeeGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [employeeGroups, page, rowsPerPage]
  );

  // Live dialog data — auto-updates after delete
  const dialogGroup = useMemo(
    () => employeeGroups.find((g) => g.employee?.id === dialogEmployeeId) || null,
    [employeeGroups, dialogEmployeeId]
  );

  const stats = useMemo(() => {
    if (!Array.isArray(docs)) return { total: 0, contract: 0, nid: 0, certificate: 0, expiring: 0 };
    const now = new Date();
    const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return {
      total: docs.length,
      contract: docs.filter((d) => d.type === "contract").length,
      nid: docs.filter((d) => d.type === "nid").length,
      certificate: docs.filter((d) => d.type === "certificate").length,
      expiring: docs.filter((d) => d.expiry_date && new Date(d.expiry_date) >= now && new Date(d.expiry_date) <= thirtyDays).length,
    };
  }, [docs]);

  // ---- Helpers ----
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return dateStr;
    }
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "contract": return <FaFileContract />;
      case "nid": return <FaIdCard />;
      case "certificate": return <FaGraduationCap />;
      case "increment": return <FaFileAlt />;
      default: return <FaFile />;
    }
  };

  const getTypeColor = (type) =>
    ({
      contract: { bg: "#dbeafe", color: "#1e40af", border: "#3b82f6" },
      nid: { bg: "#f3e8ff", color: "#6b21a8", border: "#a855f7" },
      certificate: { bg: "#dcfce7", color: "#166534", border: "#22c55e" },
      increment: { bg: "#fef3c7", color: "#92400e", border: "#f59e0b" },
    }[type?.toLowerCase()] || { bg: "#f1f5f9", color: "#475569", border: "#94a3b8" });

  const renderTypeChip = (type) => {
    const c = getTypeColor(type);
    return (
      <Chip
        key={type}
        icon={getTypeIcon(type)}
        label={type}
        size="small"
        sx={{ bgcolor: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 600, textTransform: "capitalize", "& .MuiChip-icon": { color: c.color } }}
      />
    );
  };

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { status: "none", label: "No Expiry", color: "#64748b" };
    const days = Math.ceil((new Date(expiryDate) - new Date()) / 86400000);
    if (days < 0) return { status: "expired", label: "Expired", color: "#dc2626" };
    if (days <= 30) return { status: "expiring", label: `${days}d left`, color: "#d97706" };
    return { status: "valid", label: "Valid", color: "#16a34a" };
  };

  // Worst status across an employee's documents
  const getGroupStatus = (groupDocs) => {
    let worst = { status: "none", label: "No Expiry", color: "#64748b" };
    groupDocs.forEach((d) => {
      const s = getExpiryStatus(d.expiry_date);
      if (s.status === "expired") worst = s;
      else if (s.status === "expiring" && worst.status !== "expired") worst = s;
      else if (s.status === "valid" && worst.status === "none") worst = s;
    });
    return worst;
  };

  const uniqueTypes = (groupDocs) => [...new Set(groupDocs.map((d) => d.type))];
  const uniqueVisibilities = (groupDocs) => [...new Set(groupDocs.map((d) => d.visibility || "private"))];

  const getInitials = (name) => {
    if (!name) return "N/A";
    const parts = name.split(" ");
    return parts.length >= 2 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#06b6d4", "#3b82f6"];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const requestSort = (key) =>
    setSortConfig({ key, direction: sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc" });

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort style={{ fontSize: 12, marginLeft: 4, color: "#94a3b8" }} />;
    return sortConfig.direction === "asc"
      ? <FaSortUp style={{ fontSize: 12, marginLeft: 4, color: "#6366f1" }} />
      : <FaSortDown style={{ fontSize: 12, marginLeft: 4, color: "#6366f1" }} />;
  };

  const statusChip = (status) => (
    <Chip
      label={status.label}
      size="small"
      sx={{
        bgcolor: status.status === "expired" ? "#fef2f2" : status.status === "expiring" ? "#fef3c7" : status.status === "valid" ? "#f0fdf4" : "#f1f5f9",
        color: status.color,
        fontWeight: 600,
      }}
    />
  );

  // ---- ✅ TABLE VIEW: one row per employee ----
  const renderTableView = () => (
    <StyledTableContainer>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: "#f8fafc" }}>
            <StyledTableCell width={60}>#</StyledTableCell>
            <StyledTableCell onClick={() => requestSort("employee_name")} sx={{ cursor: "pointer", userSelect: "none" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>Employee {renderSortIcon("employee_name")}</Box>
            </StyledTableCell>
            <StyledTableCell>Document Types</StyledTableCell>
            <StyledTableCell align="center">Total Docs</StyledTableCell>
            <StyledTableCell>Permissions</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell align="center">Actions</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            Array.from(new Array(5)).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton width={40} /></TableCell>
                <TableCell><Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}><Skeleton variant="circular" width={36} height={36} /><Skeleton width={120} /></Box></TableCell>
                <TableCell><Skeleton width={180} /></TableCell>
                <TableCell><Skeleton width={60} /></TableCell>
                <TableCell><Skeleton width={100} /></TableCell>
                <TableCell><Skeleton width={80} /></TableCell>
                <TableCell><Skeleton width={60} /></TableCell>
              </TableRow>
            ))
          ) : paginatedGroups.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                <FaFolderOpen style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 16 }} />
                <Typography variant="h6" color="#475569">No documents found</Typography>
                <Typography variant="body2" color="#64748b">Try adjusting filters or upload a new document</Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedGroups.map((group, index) => {
              const empName = group.employee?.employee_name || "N/A";
              const types = uniqueTypes(group.documents);
              const visibilities = uniqueVisibilities(group.documents);
              const status = getGroupStatus(group.documents);

              return (
                <StyledTableRow key={group.employee?.id || index} hover>
                  <TableCell sx={{ color: "#64748b" }}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: getColorFromName(empName), width: 40, height: 40, fontWeight: 600 }}>{getInitials(empName)}</Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} color="#1e293b">{empName}</Typography>
                        <Typography variant="caption" color="#64748b">
                          {group.employee?.department || ""} {group.employee?.position ? `• ${group.employee.position}` : ""}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {/* ✅ Multiple chips when employee has multiple types */}
                  <TableCell>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>{types.map((t) => renderTypeChip(t))}</Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={group.documents.length} size="small" sx={{ bgcolor: "#e0e7ff", color: "#4338ca", fontWeight: 700, minWidth: 36 }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>{visibilities.map((v) => renderPermissionChip(v))}</Box>
                  </TableCell>
                  <TableCell>{statusChip(status)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View All Documents">
                      <IconButton onClick={() => openEmployeeDocs(group)} sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" } }}>
                        <FaEye />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </StyledTableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      {!loading && employeeGroups.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={employeeGroups.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ borderTop: "1px solid #e2e8f0" }}
        />
      )}
    </StyledTableContainer>
  );

  // ---- GRID VIEW: one card per employee ----
  const renderGridView = () => (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2 }}>
      {loading ? (
        Array.from(new Array(8)).map((_, i) => <Skeleton key={i} variant="rectangular" height={190} sx={{ borderRadius: 2 }} />)
      ) : paginatedGroups.length === 0 ? (
        <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 8 }}>
          <FaFolderOpen style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 16 }} />
          <Typography variant="h6" color="#475569">No documents found</Typography>
        </Box>
      ) : (
        paginatedGroups.map((group) => {
          const empName = group.employee?.employee_name || "N/A";
          const types = uniqueTypes(group.documents);
          const status = getGroupStatus(group.documents);
          return (
            <DocumentCard key={group.employee?.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Avatar sx={{ bgcolor: getColorFromName(empName), width: 40, height: 40, fontWeight: 600 }}>{getInitials(empName)}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#1e293b" noWrap>{empName}</Typography>
                  <Typography variant="caption" color="#64748b" noWrap>
                    {group.employee?.department || ""} {group.employee?.position ? `• ${group.employee.position}` : ""}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>{types.map((t) => renderTypeChip(t))}</Box>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" color="#64748b" fontWeight={600}>{group.documents.length} Documents</Typography>
                {statusChip(status)}
              </Box>
              <Button fullWidth variant="contained" size="small" startIcon={<FaEye />} onClick={() => openEmployeeDocs(group)}
                sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
                View Documents
              </Button>
            </DocumentCard>
          );
        })
      )}
    </Box>
  );

  // ---- EMPLOYEE (ACCORDION) VIEW ----
  const renderEmployeeView = () => {
    if (loading) return <Box>{[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={72} sx={{ borderRadius: 2, mb: 2 }} />)}</Box>;
    if (employeeGroups.length === 0)
      return (
        <Paper sx={{ textAlign: "center", py: 8, borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "none" }}>
          <FaUsers style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 16 }} />
          <Typography variant="h6" color="#475569">No employee documents found</Typography>
        </Paper>
      );

    return (
      <Box>
        {employeeGroups.map((group) => (
          <Accordion key={group.employee?.id} sx={{ mb: 2, border: "1px solid #e2e8f0", borderRadius: "8px !important", "&:before": { display: "none" }, boxShadow: "none", overflow: "hidden" }}>
            <AccordionSummary expandIcon={<FaExpand />} sx={{ bgcolor: "#f8fafc", "&:hover": { bgcolor: "#f1f5f9" } }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, pr: 1 }}>
                <Avatar sx={{ bgcolor: getColorFromName(group.employee?.employee_name), width: 40, height: 40, fontWeight: 600 }}>{getInitials(group.employee?.employee_name)}</Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">{group.employee?.employee_name}</Typography>
                  <Typography variant="body2" color="#64748b">
                    {group.employee?.department || ""} {group.employee?.position ? `• ${group.employee.position}` : ""}
                  </Typography>
                </Box>
                <Chip label={`${group.documents.length} Documents`} size="small" sx={{ bgcolor: "#dbeafe", color: "#1e40af", fontWeight: 600 }} />
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Caption</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Issue Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Permission</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#475569" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "#475569" }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.documents.map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell>{renderTypeChip(doc.type)}</TableCell>
                      <TableCell><Typography variant="body2" color="#475569">{doc.caption || "N/A"}</Typography></TableCell>
                      <TableCell><Typography variant="body2" color="#475569">{formatDate(doc.issue_date)}</Typography></TableCell>
                      <TableCell>{renderPermissionChip(doc.visibility)}</TableCell>
                      <TableCell>{statusChip(getExpiryStatus(doc.expiry_date))}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Preview">
                            <IconButton size="small" onClick={() => setViewerDoc(doc)} sx={{ color: "#6366f1" }}>
                              <FaEye fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton size="small" onClick={() => downloadDocument(doc)} sx={{ color: "#3b82f6" }}><FaDownload fontSize="small" /></IconButton>
                          {!isEmployee && (
                            <IconButton size="small" onClick={() => deleteDocument(doc.id)} disabled={actionLoading === doc.id} sx={{ color: "#dc2626" }}><FaTrash fontSize="small" /></IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  };

  // ---- Loading Shell ----
  if (loading && docs.length === 0) {
    return (
      <MainContainer>
        <Breadcrumb title="Documents" />
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 3 }} />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 2, mb: 3 }}>
          {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} variant="rectangular" height={100} sx={{ borderRadius: 2 }} />)}
        </Box>
      </MainContainer>
    );
  }

  // ---- Main Render ----
  return (
    <MainContainer>
      <Breadcrumb title="Documents" />

      {/* Header */}
      <HeaderCard elevation={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>Document Management</Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>Securely manage and organize employee documents</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadData} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}><FaSync /></IconButton>
            </Tooltip>
            {!isEmployee && (
              <ActionButton variant="contained" startIcon={<FaUpload />}
                onClick={() => document.getElementById("upload-section")?.scrollIntoView({ behavior: "smooth" })}
                sx={{ bgcolor: "white", color: "#6366f1", "&:hover": { bgcolor: "#f0f4ff" } }}>
                Upload Documents
              </ActionButton>
            )}
          </Stack>
        </Box>
      </HeaderCard>

      {/* Stats */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", lg: "repeat(5, 1fr)" }, gap: 2, mb: 3 }}>
        {[
          { icon: <FaFolderOpen size={20} color="#1e40af" />, bg: "#dbeafe", value: stats.total, label: "Total Docs" },
          { icon: <FaFileContract size={20} color="#1e40af" />, bg: "#dbeafe", value: stats.contract, label: "Contracts" },
          { icon: <FaIdCard size={20} color="#6b21a8" />, bg: "#f3e8ff", value: stats.nid, label: "NID Docs" },
          { icon: <FaGraduationCap size={20} color="#166534" />, bg: "#dcfce7", value: stats.certificate, label: "Certificates" },
          { icon: <FaExclamationTriangle size={20} color="#92400e" />, bg: "#fef3c7", value: stats.expiring, label: "Expiring Soon" },
        ].map((s, i) => (
          <StatCard key={i}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: s.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>{s.icon}</Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color="#1e293b">{s.value}</Typography>
                <Typography variant="caption" color="#64748b" fontWeight={500}>{s.label}</Typography>
              </Box>
            </Box>
          </StatCard>
        ))}
      </Box>

      {/* Upload Form */}
      {!isEmployee && (
        <Paper id="upload-section" sx={{ p: 3, mb: 3, borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
            <FaUpload style={{ marginRight: 8, verticalAlign: "middle" }} />Upload New Documents
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Employee *</InputLabel>
                <Select value={employeeId} label="Select Employee *" onChange={(e) => setEmployeeId(e.target.value)}>
                  {employees.map((emp) => (
                    <MenuItem key={emp.value} value={emp.value}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5 }}>
                        <Avatar sx={{ bgcolor: getColorFromName(emp.label), width: 28, height: 28, fontSize: "0.75rem" }}>{getInitials(emp.label)}</Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{emp.label}</Typography>
                          {emp.department && <Typography variant="caption" color="#64748b">{emp.department} • {emp.position}</Typography>}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {documents.map((doc, index) => (
              <React.Fragment key={index}>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, mb: 1 }}>
                    <Chip label={`Document ${index + 1}`} size="small" sx={{ bgcolor: "#dbeafe", color: "#1e40af", fontWeight: 600 }} />
                    {documents.length > 1 && (
                      <Button size="small" color="error" startIcon={<FaTrash />} onClick={() => removeDocument(index)} sx={{ textTransform: "none" }}>Remove</Button>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(5, 1fr)" }, gap: 2 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Type *</InputLabel>
                      <Select value={doc.type} label="Type *" onChange={(e) => handleDocChange(index, "type", e.target.value)}>
                        <MenuItem value="contract"><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><FaFileContract /> Contract</Box></MenuItem>
                        <MenuItem value="nid"><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><FaIdCard /> NID</Box></MenuItem>
                        <MenuItem value="certificate"><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><FaGraduationCap /> Certificate</Box></MenuItem>
                        <MenuItem value="increment"><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><FaFileAlt /> Increment</Box></MenuItem>
                        <MenuItem value="other"><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><FaFile /> Other</Box></MenuItem>
                      </Select>
                    </FormControl>
                    <Box sx={{
                      border: "2px dashed #cbd5e1", borderRadius: 1, padding: "10px 16px", display: "flex", alignItems: "center", gap: 1.5,
                      background: doc.file ? "#f0fdf4" : "#f8fafc", transition: "all 0.2s", cursor: "pointer", position: "relative", height: 40,
                      borderColor: doc.file ? "#22c55e" : "#cbd5e1",
                      "&:hover": { borderColor: "#6366f1", background: "#f0f4ff" },
                      "& input": { position: "absolute", top: 0, left: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" },
                    }}>
                      <input type="file" onChange={(e) => handleDocChange(index, "file", e.target.files[0])} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" />
                      <FaFileAlt style={{ fontSize: 16, color: doc.file ? "#22c55e" : "#64748b" }} />
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>{doc.file ? doc.file.name : "Choose File *"}</Typography>
                    </Box>
                    <TextField fullWidth label="Caption" placeholder="e.g., Salary Increment 2024" value={doc.caption} onChange={(e) => handleDocChange(index, "caption", e.target.value)} size="small" />
                    <TextField fullWidth label="Issue Date" type="date" value={doc.issue_date} onChange={(e) => handleDocChange(index, "issue_date", e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
                    <FormControl size="small" fullWidth>
                      <InputLabel>Visibility</InputLabel>
                      <Select value={doc.visibility} label="Visibility" onChange={(e) => handleDocChange(index, "visibility", e.target.value)}>
                        <MenuItem value="private"><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><FaLock /> Private</Box></MenuItem>
                        <MenuItem value="restricted"><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><FaUserShield /> Restricted</Box></MenuItem>
                        <MenuItem value="public"><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><FaUnlock /> Public</Box></MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              </React.Fragment>
            ))}

            <Grid item xs={12}>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <Button variant="outlined" startIcon={<FaPlus />} onClick={addMoreDocument}
                  sx={{ textTransform: "none", borderColor: "#cbd5e1", color: "#475569", "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" } }}>
                  Add Another Document
                </Button>
                <ActionButton variant="contained" onClick={upload} disabled={uploadLoading} startIcon={uploadLoading ? null : <FaUpload />}
                  sx={{ height: 48, px: 4, bgcolor: "#6366f1", "&:hover": { bgcolor: "#4f46e5" } }}>
                  {uploadLoading ? "Uploading..." : "Upload All Documents"}
                </ActionButton>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Filters & View Switcher */}
      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ flex: 1, minWidth: 0 }}>
            <TextField placeholder="Search documents..." value={search} onChange={(e) => setSearch(e.target.value)} size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><FaSearch style={{ color: "#94a3b8" }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 200, "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc" } }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Employee</InputLabel>
              <Select value={selectedEmployee} label="Employee" onChange={(e) => { setSelectedEmployee(e.target.value); setPage(0); }}>
                <MenuItem value="all">All Employees</MenuItem>
                {employees.map((emp) => <MenuItem key={emp.value} value={emp.value}>{emp.label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select value={typeFilter} label="Type" onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="contract">Contract</MenuItem>
                <MenuItem value="nid">NID</MenuItem>
                <MenuItem value="certificate">Certificate</MenuItem>
                <MenuItem value="increment">Increment</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Permission</InputLabel>
              <Select value={visibilityFilter} label="Permission" onChange={(e) => { setVisibilityFilter(e.target.value); setPage(0); }}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="restricted">Restricted</MenuItem>
                <MenuItem value="public">Public</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Table View"><ViewToggleButton active={viewMode === "table"} onClick={() => switchView("table")}><FaList /></ViewToggleButton></Tooltip>
            <Tooltip title="Grid View"><ViewToggleButton active={viewMode === "grid"} onClick={() => switchView("grid")}><FaTh /></ViewToggleButton></Tooltip>
            <Tooltip title="Employee View"><ViewToggleButton active={viewMode === "employee"} onClick={() => switchView("employee")}><FaUsers /></ViewToggleButton></Tooltip>
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {viewMode === "table" && renderTableView()}
      {viewMode === "grid" && renderGridView()}
      {viewMode === "employee" && renderEmployeeView()}

      {/* ✅ EMPLOYEE DOCUMENTS DIALOG — shows ALL documents of the employee */}
      <Dialog open={employeeDialogOpen} onClose={() => setEmployeeDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 2 }}>
          {dialogGroup ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar sx={{ bgcolor: getColorFromName(dialogGroup.employee?.employee_name), width: 48, height: 48, fontWeight: 700 }}>
                {getInitials(dialogGroup.employee?.employee_name)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={700} color="#1e293b">{dialogGroup.employee?.employee_name}</Typography>
                <Typography variant="body2" color="#64748b">
                  {dialogGroup.employee?.department || ""} {dialogGroup.employee?.position ? `• ${dialogGroup.employee.position}` : ""}
                </Typography>
              </Box>
              <Chip label={`${dialogGroup.documents.length} Documents`} sx={{ bgcolor: "#e0e7ff", color: "#4338ca", fontWeight: 700 }} />
              <IconButton onClick={() => setEmployeeDialogOpen(false)}><FaTimes /></IconButton>
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6" fontWeight={700}>Documents</Typography>
              <IconButton onClick={() => setEmployeeDialogOpen(false)}><FaTimes /></IconButton>
            </Box>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: "#f8fafc", p: 0 }}>
          {dialogGroup && dialogGroup.documents.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <StyledTableCell>Type</StyledTableCell>
                  <StyledTableCell>Caption</StyledTableCell>
                  <StyledTableCell>Issue Date</StyledTableCell>
                  <StyledTableCell>Expiry Date</StyledTableCell>
                  <StyledTableCell>Permission</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell align="center">Actions</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dialogGroup.documents.map((doc) => (
                  <TableRow key={doc.id} hover sx={{ bgcolor: "white" }}>
                    <TableCell>{renderTypeChip(doc.type)}</TableCell>
                    <TableCell><Typography variant="body2" color="#475569" sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.caption || "N/A"}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="#475569">{formatDate(doc.issue_date)}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="#475569">{formatDate(doc.expiry_date)}</Typography></TableCell>
                    <TableCell>{renderPermissionChip(doc.visibility)}</TableCell>
                    <TableCell>{statusChip(getExpiryStatus(doc.expiry_date))}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Preview">
                          <IconButton size="small" onClick={() => setViewerDoc(doc)} sx={{ color: "#6366f1" }}>
                            <FaEye fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton size="small" onClick={() => downloadDocument(doc)} sx={{ color: "#3b82f6" }}><FaDownload fontSize="small" /></IconButton>
                        </Tooltip>
                        {!isEmployee && (
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => deleteDocument(doc.id)} disabled={actionLoading === doc.id} sx={{ color: "#dc2626" }}><FaTrash fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <FaFolderOpen style={{ fontSize: 48, color: "#cbd5e1", marginBottom: 16 }} />
              <Typography variant="h6" color="#475569">No documents</Typography>
              <Typography variant="body2" color="#64748b">All documents for this employee have been removed.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEmployeeDialogOpen(false)} sx={{ textTransform: "none", fontWeight: 600 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Secure Document Viewer (opens on top of any dialog) */}
      <DocumentViewer open={!!viewerDoc} doc={viewerDoc} onClose={() => setViewerDoc(null)} />
    </MainContainer>
  );
};

export default DocumentManager;